<template lang="pug">
.business-card(@click="openDrawer")
    .card-inner
        .card-header
            .card-logo(v-if="previewImage")
                img(:src="previewImageSrc")
            .card-logo.placeholder(v-else)
                i.el-icon-fa-building.fas
            .card-title
                h3.company-name {{ headerTitle }}
                .branch-subtitle(v-if="branchLabel") {{ branchLabel }}
                .category-tag(v-if="categoryName")
                    i(v-if="categoryIcon" :class="categoryIcon")
                    span {{ categoryName }}
        .card-body
            .card-description(v-if="descriptionPlainText") {{ descriptionPlainText }}
            .card-info
                .info-row(v-if="addressOneLiner")
                    i.el-icon-fa-map-marker-alt.fas
                    span {{ addressOneLiner }}
                .info-row(v-if="homepageUrl")
                    i.el-icon-fa-globe.fas
                    a(:href="homepageUrl" target="_blank" @click.stop) {{ homepageUrlDisplay }}
                .info-row(v-if="hasOpeningHours")
                    i.el-icon-fa-clock.far
                    span {{ $t('Opening hours set') }}
        .card-footer
            span.indexed-at-info(v-if="indexedAt")
                | {{ $t('Last indexed at') }}: {{ indexedAt }}
            span.indexed-at-info.pending(v-if="indexDirtySince")
                | {{ $t('Indexing pending since') }}: {{ indexDirtySince }}
            span.edit-hint
                i.el-icon-fa-pencil-alt.fas
                | {{ $t('Click to edit') }}

    el-drawer(
        :visible.sync="drawerVisible"
        :title="$t('Edit Business Card')"
        direction="rtl"
        size="500px"
        :before-close="handleDrawerClose"
        custom-class="business-card-drawer"
        append-to-body
    )
        .drawer-wrapper
            el-form.drawer-form(
                ref="drawerForm"
                label-position="top"
                :model="localBc"
                :rules="drawerRules"
                :validate-on-rule-change="false"
                @submit.native.prevent
            )
                .drawer-content
                    BusinessCardForm(
                        :value="localBc"
                        :cms="cms"
                        propPath=""
                        :category-options="categories"
                        :tag-options="metaTagSuggestions"
                        @remove-address="removeAddress"
                    )
                        template(#address-fallback)
                            .fallback-panel
                                .flex-row
                                    .info.grow
                                        .fallback-label {{ fallbackAddressLabel }}
                                        .fallback-name(v-if="fallbackAddressName") {{ fallbackAddressName }}
                                        .fallback-address(v-if="fallbackAddressOneLiner") {{ fallbackAddressOneLiner }}
                                        .no-address(v-if="!fallbackAddressName && !fallbackAddressOneLiner") {{ $t('No address available') }}
                                    .actions
                                        el-button(
                                            plain
                                            circle
                                            size="mini"
                                            type="primary"
                                            icon="el-icon-fa-pencil-alt fas"
                                            @click="addAddress"
                                        )

            .drawer-footer
                el-button(@click="drawerVisible = false") {{ $t('Cancel') }}
                el-button(
                    type="primary"
                    :loading="saving"
                    @click="save"
                ) {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "Business Card": "Visitenkarte",
        "Edit Business Card": "Visitenkarte bearbeiten",
        "Save": "Speichern",
        "Cancel": "Abbrechen",
        "Saved successfully": "Erfolgreich gespeichert",
        "Save failed": "Speichern fehlgeschlagen",
        "Correct your input and submit again.": "Bitte Eingabe korrigieren und erneut speichern.",
        "Owner address": "Inhaberadresse",
        "Stock address": "Lageradresse",
        "No address available": "Keine Adresse vorhanden",
        "Click to edit": "Klicken zum Bearbeiten",
        "Opening hours set": "Öffnungszeiten hinterlegt",
        "Last indexed at": "Zuletzt indiziert am",
        "Indexing pending since": "Indizierung ausstehend seit"
    }
}
</i18n>

<script>
import Vue from 'vue'
import _ from 'lodash'
import striptags from 'striptags'
import uniqid from 'uniqid'

import { formatDate } from '@/filters'
import { parseISO } from 'date-fns'
import { getFitStringFromProcessObject } from '@/helpers'
import CmsApi from '@/store/api/cms/cms'
import WizardApi from '@/store/api/cms/wizard'
import { useSetupStore, useAccountStore } from '@/stores'

import BusinessCardForm from '@/components/businessCard/BusinessCardForm'

const emptyOpeningHours = () => ({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
})

const emptyBc = () => ({
    name: '',
    description: '',
    category: null,
    homepage_url: '',
    phone: '',
    email: '',
    opening_hours: emptyOpeningHours(),
    can_visit_location: false,
    has_delivery_service: false,
    meta_tags: [],
    image: null,
    address: null,
})

export default {
    name: 'BusinessCardPanel',
    components: {
        BusinessCardForm,
    },
    props: {
        cms: {
            type: Object,
            required: true,
        },
        // When provided, this panel edits a specific BusinessCard instance
        // (chain-of-stores branches). When omitted, falls back to
        // `cms.businessCard` (single-card mode).
        businessCard: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            drawerVisible: false,
            saving: false,
            localBc: emptyBc(),
            categories: [],
            categoriesLoading: false,
            metaTagSuggestions: [],
            metaTagsLoading: false,
        }
    },
    computed: {
        activeBusinessCard() {
            return this.businessCard || _.get(this.cms, 'businessCard') || null
        },
        isBranchPanel() {
            return !!this.businessCard
        },
        branchLabel() {
            if (!this.isBranchPanel) return ''
            return _.get(this.businessCard, 'stock.name')
                || _.get(this.businessCard, 'stock.key')
                || ''
        },
        headerTitle() {
            return _.get(this.activeBusinessCard, 'effective_name')
                || _.get(this.activeBusinessCard, 'name')
                || _.get(this.cms, 'name')
                || ''
        },

        // ---- Preview card readouts (active BC) ----
        categoryName() {
            return _.get(this.activeBusinessCard, 'category.name', null)
        },
        categoryIcon() {
            return _.get(this.activeBusinessCard, 'category.icon', null)
        },
        previewImage() {
            return _.get(this.activeBusinessCard, 'image', null)
        },
        previewImageSrc() {
            const image = this.previewImage
            if (!image) return ''
            const publicStorageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PUBLIC_STORAGE_ROUTE}`, '/')
            let fit = 'fit=crop&crop=focalpoint&w=150&h=150&x=.5&y=.5&z=1'
            const processing = _.get(image, 'settings.processing')
            if (processing) {
                fit = getFitStringFromProcessObject(processing)
            }
            return `${publicStorageRoute}/images/${image.id}/${image.name}?${fit}`
        },
        descriptionPlainText() {
            const desc = _.get(this.activeBusinessCard, 'description', '') || ''
            if (!desc) return ''
            const stripped = striptags(desc)
            return stripped.substring(0, 100) + (stripped.length > 100 ? '...' : '')
        },
        addressOneLiner() {
            const ownAddress = _.get(this.activeBusinessCard, 'address')
            const stockAddress = _.get(this.activeBusinessCard, 'stock.address')
            const address = (ownAddress && (ownAddress.street || ownAddress.city)) ? ownAddress : stockAddress
            if (!address) return ''
            const parts = []
            if (address.street) parts.push(address.street)
            if (address.zipcode || address.city) {
                parts.push([address.zipcode, address.city].filter(Boolean).join(' '))
            }
            return parts.join(', ')
        },
        homepageUrl() {
            return _.get(this.activeBusinessCard, 'homepage_url', '') || ''
        },
        homepageUrlDisplay() {
            return this.homepageUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')
        },
        hasOpeningHours() {
            const hours = _.get(this.activeBusinessCard, 'opening_hours')
            if (!hours) return false
            return Object.values(hours).some((day) => day && day.length > 0)
        },
        indexedAt() {
            const value = _.get(this.activeBusinessCard, 'indexed_at')
            if (!value) return null
            return formatDate(parseISO(value), 'dd.MM.yyyy, HH:mm:ss')
        },
        indexDirtySince() {
            const value = _.get(this.activeBusinessCard, 'index_dirty_since')
            if (!value) return null
            return formatDate(parseISO(value), 'dd.MM.yyyy, HH:mm:ss')
        },

        // ---- Drawer fallback panel (Owner vs Stock) ----
        fallbackAddressLabel() {
            return this.isBranchPanel ? this.$t('Stock address') : this.$t('Owner address')
        },
        fallbackAddressName() {
            if (!this.isBranchPanel) {
                const accountForm = useAccountStore().form
                const parts = [
                    _.get(accountForm, 'first_name'),
                    _.get(accountForm, 'last_name'),
                ].filter(Boolean)
                if (parts.length) return parts.join(' ')
                return _.get(accountForm, 'company', '') || ''
            }
            const stockAddress = _.get(this.activeBusinessCard, 'stock.address')
            if (!stockAddress) return ''
            const parts = [stockAddress.first_name, stockAddress.last_name].filter(Boolean).join(' ')
            return parts || stockAddress.company || _.get(this.activeBusinessCard, 'stock.name') || ''
        },
        fallbackAddressOneLiner() {
            const address = this.isBranchPanel
                ? _.get(this.activeBusinessCard, 'stock.address')
                : _.get(useAccountStore().form, 'addresses[0]')
            if (!address) return ''
            const parts = []
            if (address.street) parts.push(address.street)
            if (address.zipcode || address.city) {
                parts.push([address.zipcode, address.city].filter(Boolean).join(' '))
            }
            return parts.join(', ')
        },

        // ---- Drawer validation rules ----
        drawerRules() {
            const rules = {
                description: 'max:300',
                category: 'required',
                homepage_url: 'url',
                email: 'email',
            }
            if (this.localBc.address && !this.localBc.address.deleted) {
                rules.address = {
                    street: 'required',
                    zipcode: 'required',
                    city: 'required',
                    country: 'required',
                }
            }
            return rules
        },
    },
    watch: {
        cms: {
            immediate: true,
            handler(newCms) {
                if (newCms) {
                    this.initializeFromCms(newCms)
                }
            },
        },
    },
    async created() {
        await this.fetchCategories()
        this.fetchBusinessCardTags()
    },
    methods: {
        openDrawer() {
            this.initializeFromCms(this.cms)
            this.drawerVisible = true
        },
        handleDrawerClose(done) {
            done()
        },
        initializeFromCms(cms) {
            const bc = this.activeBusinessCard || {}
            this.localBc = {
                name: _.get(bc, 'name', '') || '',
                description: _.get(bc, 'description', '') || '',
                category: _.get(bc, 'category') ? _.cloneDeep(bc.category) : null,
                homepage_url: _.get(bc, 'homepage_url', '') || '',
                phone: _.get(bc, 'phone', '') || '',
                email: _.get(bc, 'email', '') || '',
                opening_hours: _.cloneDeep(_.get(bc, 'opening_hours')) || emptyOpeningHours(),
                can_visit_location: !!_.get(bc, 'can_visit_location'),
                has_delivery_service: !!_.get(bc, 'has_delivery_service'),
                meta_tags: _.cloneDeep(_.get(bc, 'meta_tags', [])) || [],
                image: _.cloneDeep(_.get(bc, 'image')) || null,
                address: _.cloneDeep(_.get(bc, 'address')) || null,
            }
        },
        addAddress() {
            Vue.set(this.localBc, 'address', {
                id: uniqid('new:'),
                street: '',
                zipcode: '',
                city: '',
                country: 'DE',
                latitude: null,
                longitude: null,
                type: 'business-card',
            })
        },
        removeAddress() {
            const address = this.localBc.address
            if (address && address.id && String(address.id).indexOf('new:') !== 0) {
                Vue.set(address, 'deleted', true)
            } else {
                Vue.set(this.localBc, 'address', null)
            }
        },
        async fetchCategories() {
            this.categoriesLoading = true
            try {
                const categories = await WizardApi.fetchBusinessCardCategories()
                this.categories = (categories || []).map((cat) => ({
                    ...cat,
                    id: parseInt(cat.id, 10),
                }))
            } catch (error) {
                console.error('Failed to fetch categories:', error)
            } finally {
                this.categoriesLoading = false
            }
        },
        async fetchBusinessCardTags() {
            this.metaTagsLoading = true
            try {
                const tags = await WizardApi.fetchBusinessCardTags()
                this.metaTagSuggestions = tags || []
            } catch (error) {
                console.error('Failed to fetch business card tags:', error)
            } finally {
                this.metaTagsLoading = false
            }
        },
        updateCmsInAccountStore(updatedCms) {
            const accountStore = useAccountStore()
            const administrators = _.get(accountStore.form, 'administrators', [])
            const adminIndex = administrators.findIndex((admin) => {
                const cms = _.get(admin, 'cms')
                return cms && cms.id === updatedCms.id
            })
            if (adminIndex >= 0) {
                Vue.set(accountStore.form.administrators[adminIndex], 'cms', updatedCms)
            }
        },
        buildBusinessCardPayload() {
            const bc = this.localBc
            const payload = {
                name: bc.name || null,
                description: bc.description || null,
                category: bc.category && bc.category.id ? { id: parseInt(bc.category.id, 10) } : null,
                homepage_url: bc.homepage_url || null,
                phone: bc.phone || null,
                email: bc.email || null,
                opening_hours: bc.opening_hours,
                can_visit_location: !!bc.can_visit_location,
                has_delivery_service: !!bc.has_delivery_service,
                meta_tags: (bc.meta_tags || []).map((tag, index) => ({
                    id: tag.id || ('new:' + index),
                    key: tag.key,
                    name: tag.name,
                })),
            }

            // Image: send if present, mark deleted when applicable.
            const previousImage = _.get(this.activeBusinessCard, 'image')
            if (bc.image && !bc.image.deleted) {
                payload.image = {
                    id: bc.image.id,
                    type: bc.image.type,
                    upload: bc.image.upload || false,
                    settings: bc.image.settings || null,
                    title: bc.image.title || null,
                    alt: bc.image.alt || null,
                    width: bc.image.width || null,
                    height: bc.image.height || null,
                    // source.id lets the backend attach a library selection (new:file-library:).
                    source: bc.image.source ? { id: parseInt(bc.image.source.id, 10) || bc.image.source.id } : null,
                }
            } else if (previousImage) {
                payload.image = {
                    id: previousImage.id,
                    deleted: true,
                }
            }

            // Address: only send when there's content or an explicit deletion.
            if (bc.address) {
                const hasContent = bc.address.street || bc.address.city || bc.address.zipcode
                if (hasContent || bc.address.deleted) {
                    payload.address = bc.address
                }
            }

            return payload
        },
        async save() {
            const formRef = this.$refs.drawerForm
            const valid = await new Promise((resolve) => {
                if (!formRef) {
                    resolve(true)
                    return
                }
                formRef.validate((isValid) => resolve(isValid))
            })
            if (!valid) {
                this.$notify.error({
                    title: this.$t('Correct your input and submit again.'),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 4000,
                })
                return
            }

            this.saving = true
            try {
                const businessCardPayload = this.buildBusinessCardPayload()
                const cmsData = { id: this.cms.id }

                if (this.isBranchPanel) {
                    // Per-stock: send the full businessCards array with the
                    // edited entry replacing the matching id, siblings as
                    // stock-id-only stubs so applyBusinessCardMode keeps them.
                    const editedId = parseInt(_.get(this.businessCard, 'id'), 10)
                    cmsData.has_business_card_per_stock = true
                    cmsData.businessCards = (_.get(this.cms, 'businessCards', []) || []).map((branch) => {
                        if (parseInt(branch.id, 10) === editedId) {
                            return {
                                ...businessCardPayload,
                                stock: { id: parseInt(_.get(branch, 'stock.id'), 10) },
                            }
                        }
                        return {
                            stock: { id: parseInt(_.get(branch, 'stock.id'), 10) },
                        }
                    })
                } else {
                    cmsData.businessCard = businessCardPayload
                }

                const updatedCms = await CmsApi.update(cmsData)
                this.updateCmsInAccountStore(updatedCms)
                this.fetchBusinessCardTags()

                this.$notify.success({
                    title: this.$t('Saved successfully'),
                    iconClass: 'fas el-icon-fa-check',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 4000,
                })

                this.drawerVisible = false
                await useSetupStore().fetchSetup()
            } catch (error) {
                console.error('Save failed:', error)
                this.$notify.error({
                    title: this.$t('Save failed'),
                    iconClass: 'fas el-icon-fa-times',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 4000,
                })
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style lang="sass" scoped>
.business-card
    width: 360px
    min-height: 200px
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)
    border: 1px solid #e2e8f0
    border-radius: 8px
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)
    cursor: pointer
    transition: all 0.25s ease
    overflow: hidden
    position: relative

    &:hover
        transform: translateY(-4px)
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)
        border-color: #409eff

.card-inner
    padding: 20px
    padding-bottom: 10px
    height: 100%
    display: flex
    flex-direction: column

.card-header
    display: flex
    align-items: flex-start
    gap: 14px
    margin-bottom: 14px

.card-logo
    width: 56px
    height: 56px
    border-radius: 8px
    overflow: hidden
    background: #f1f5f9
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    border: 1px solid #e2e8f0
    img
        width: 100%
        height: 100%
        object-fit: cover
    &.placeholder
        background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)
        i
            font-size: 24px
            color: #94a3b8

.card-title
    flex: 1
    min-width: 0
    .company-name
        margin: 0 0 6px 0
        font-size: 17px
        font-weight: 600
        color: #1e293b
        line-height: 1.3
        word-wrap: break-word
    .branch-subtitle
        font-size: 12px
        color: #64748b
        margin-bottom: 4px
    .category-tag
        display: inline-flex
        align-items: center
        gap: 5px
        background: #dbeafe
        color: #1d4ed8
        font-size: 11px
        font-weight: 500
        padding: 3px 8px
        border-radius: 4px
        i
            font-size: 10px

.card-body
    flex: 1

.card-description
    font-size: 12px
    color: #64748b
    line-height: 1.5
    margin-bottom: 12px
    display: -webkit-box
    -webkit-line-clamp: 2
    -webkit-box-orient: vertical
    overflow: hidden

.card-info
    display: flex
    flex-direction: column
    gap: 6px

.info-row
    display: flex
    align-items: center
    font-size: 12px
    color: #475569
    i
        width: 16px
        margin-right: 8px
        color: #94a3b8
        font-size: 11px
        text-align: center
    a
        color: #2563eb
        text-decoration: none
        &:hover
            text-decoration: underline

.card-footer
    margin-top: 12px
    padding-top: 10px
    border-top: 1px solid #e2e8f0
    .indexed-at-info
        display: block
        text-align: center
        font-size: 10px
        color: #94a3b8
        margin-bottom: 4px
        &.pending
            color: #e6a23c
    .edit-hint
        display: flex
        align-items: center
        justify-content: center
        gap: 6px
        font-size: 11px
        color: #94a3b8
        i
            font-size: 10px

// Drawer styles
.drawer-wrapper
    display: flex
    flex-direction: column
    height: 100%

.drawer-form
    flex: 1
    display: flex
    flex-direction: column
    min-height: 0

.drawer-content
    flex: 1
    overflow-y: auto
    padding: 0 20px 20px

.drawer-footer
    flex-shrink: 0
    padding: 15px 20px
    border-top: 1px solid #ebeef5
    text-align: right
    background: #fff

.fallback-panel
    background: #f5f7fa
    border-radius: 4px
    padding: 12px 16px
    min-height: 36px
    line-height: 1.3
    .flex-row
        display: flex
        gap: 20px
        align-items: center
        .grow
            flex: 1
    .info
        .fallback-label
            font-size: 0.75rem
            color: #909399
            letter-spacing: 0.5px
            margin-bottom: 4px
        .fallback-name
            font-weight: 500
            color: #303133
        .fallback-address
            color: #606266
            font-size: 0.9rem
        .no-address
            color: #909399
            font-style: italic
</style>

<style lang="sass">
// Non-scoped — drawer custom-class lives outside the component's scope
.business-card-drawer
    .el-drawer__body
        padding: 0
        height: calc(100% - 60px)
        overflow: hidden
</style>
